import { createMemo, createResource, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ArrowLeft, Camera, ImageOff } from "lucide-solid";

import { Link } from "@/lib/u-router";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import { getProductGalleryQuery, parseMedia, queryKeys } from "@famidex/shared";
import { fetchCatalogProduct } from "@/features/catalog/data/catalog-rest";
import { fetchProductGallery } from "../data/community-rest";

interface GalleryItem {
  id: string;
  cover: string | null;
  caption: string | null;
  authorName: string | null;
}

/**
 * 📣 Galería comunitaria de un producto — SLOT ADITIVO del detalle: publicaciones
 * de la comunidad etiquetadas con este set. Ruta propia (no edita el detalle de
 * Wave 0). Local-first con cuenta (PowerSync), guest-first sin ella (REST).
 * Traducción de `detalle_del_producto_con_galeria_comunitaria`.
 */
export function CommunityGalleryPage(props: { productId: string }) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const isAuthed = () => session.isAuthenticated();

  const [product] = createResource(
    () => props.productId,
    async (id: string) => fetchCatalogProduct(id),
  );

  const localRows = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()) && isAuthed(),
    queryKey: () => queryKeys.community.productGallery(props.productId),
    queryFn: () => {
      const d = drizzle();
      return d ? getProductGalleryQuery(d, props.productId) : null;
    },
  });

  const [galleryR] = createResource(
    () => (isAuthed() ? null : props.productId),
    async (id: string) => fetchProductGallery(id),
  );

  const items = createMemo<GalleryItem[]>(() => {
    if (isAuthed()) {
      return (localRows.data ?? []).map((p) => ({
        id: p.id,
        cover: parseMedia(p.media)[0] ?? null,
        caption: p.caption,
        authorName: p.author_name,
      }));
    }
    return (galleryR() ?? []).map((p) => ({
      id: p.id,
      cover: p.media[0] ?? null,
      caption: p.caption ?? null,
      authorName: p.authorName ?? null,
    }));
  });

  return (
    <div class="flex flex-col gap-5" data-testid="community-gallery">
      <Link
        to="/catalog/$productId"
        params={{ productId: props.productId }}
        class="inline-flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft class="h-4 w-4" />
        {t(msg`Volver al set`)}
      </Link>

      <header>
        <h1 class="text-2xl font-semibold text-foreground">{t(msg`Galería comunitaria`)}</h1>
        <p class="mt-1 text-sm text-muted-foreground">
          <Show when={product()} fallback={t(msg`Fotos de coleccionistas`)}>
            {(p) => {
              const name = p().editionName;
              return t(msg`Fotos de la comunidad de ${name}`);
            }}
          </Show>
        </p>
      </header>

      <Show
        when={items().length > 0}
        fallback={
          <div class="grid place-items-center gap-2 rounded-xl border border-dashed border-border/70 bg-card/40 py-16 text-center text-muted-foreground">
            <ImageOff class="h-8 w-8" />
            <p class="text-sm">{t(msg`Aún no hay fotos de la comunidad para este set.`)}</p>
            <Link
              to="/community/new"
              class="mt-1 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
            >
              <Camera class="h-4 w-4" />
              {t(msg`Sé el primero`)}
            </Link>
          </div>
        }
      >
        <section class="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <For each={items()}>
            {(item) => (
              <Link
                to="/community/$postId"
                params={{ postId: item.id }}
                class="group relative aspect-square overflow-hidden rounded-xl border border-border/60 bg-muted"
              >
                <Show
                  when={item.cover}
                  fallback={
                    <div class="grid h-full w-full place-items-center p-2 text-center text-xs text-muted-foreground">
                      {item.caption ?? item.authorName}
                    </div>
                  }
                >
                  <img
                    src={item.cover ?? ""}
                    alt={item.caption ?? ""}
                    loading="lazy"
                    class="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                </Show>
              </Link>
            )}
          </For>
        </section>
      </Show>
    </div>
  );
}
